import { MONETIZATION_CATALOG } from './catalog.ts';
import type {
  LocalSandboxScenario,
  MonetizationAdapter,
  MonetizationAvailability,
  MonetizationOffer,
  MonetizationOfferId,
  PurchaseRequest,
  PurchaseResult,
  RestoreResult,
  RewardedAdRequest,
  RewardedAdResult,
  WalletSnapshot,
} from './types.ts';

const STORAGE_VERSION = 1;

interface SandboxStoredState {
  readonly version: 1;
  readonly viewerId: string;
  readonly balance: number;
  readonly passOwned: boolean;
  readonly journal: Readonly<Record<string, PurchaseResult | RewardedAdResult>>;
  readonly updatedAt: string;
}

function wallet(balance: number, updatedAt = new Date().toISOString()): WalletSnapshot {
  return { balance, currency: 'credits', updatedAt };
}

function unavailableWallet(): WalletSnapshot {
  return wallet(0);
}

export class UnavailableMonetizationAdapter implements MonetizationAdapter {
  readonly environment = 'unavailable' as const;

  async getAvailability(): Promise<MonetizationAvailability> {
    return {
      state: 'platform_unavailable',
      reason: 'Optional styles are unavailable until Buncha Games monetization is connected.',
    };
  }

  async getWallet(): Promise<WalletSnapshot> {
    return unavailableWallet();
  }

  async listOffers(): Promise<readonly MonetizationOffer[]> {
    return [];
  }

  async purchase(_request: PurchaseRequest): Promise<PurchaseResult> {
    return {
      ok: false,
      code: 'unavailable',
      message: 'Purchases are unavailable. The free version is still ready.',
      retryable: false,
      wallet: unavailableWallet(),
    };
  }

  async showRewardedAd(_request: RewardedAdRequest): Promise<RewardedAdResult> {
    return {
      ok: false,
      code: 'unavailable',
      message: 'Rewarded ads are unavailable. The free version is still ready.',
      retryable: false,
    };
  }

  async restoreEntitlements(): Promise<RestoreResult> {
    return {
      ok: false,
      durableEntitlements: [],
      message: 'Purchase restore is unavailable.',
    };
  }
}

export interface LocalSandboxMonetizationAdapterOptions {
  readonly viewerId: string;
  readonly initialBalance?: number;
  readonly storage?: Storage | null;
  readonly scenario?: LocalSandboxScenario;
}

export class LocalSandboxMonetizationAdapter implements MonetizationAdapter {
  readonly environment = 'local_sandbox' as const;

  private readonly storage: Storage | null;
  private readonly storageKey: string;
  private scenario: LocalSandboxScenario;
  private state: SandboxStoredState;

  constructor(private readonly options: LocalSandboxMonetizationAdapterOptions) {
    this.storage = options.storage ?? (typeof localStorage === 'undefined' ? null : localStorage);
    this.storageKey = `buncha-coasters:monetization:v${STORAGE_VERSION}:${options.viewerId}`;
    this.scenario = options.scenario ?? 'success';
    this.state = this.load();
  }

  setScenario(scenario: LocalSandboxScenario): void {
    this.scenario = scenario;
  }

  async getAvailability(): Promise<MonetizationAvailability> {
    if (this.scenario === 'offline')
      return { state: 'offline', reason: 'The QA wallet is temporarily offline.' };
    if (this.scenario === 'revoked')
      return { state: 'ineligible', reason: 'The QA entitlement has been revoked.' };
    return { state: 'available', environment: 'local_sandbox' };
  }

  async getWallet(): Promise<WalletSnapshot> {
    return wallet(this.state.balance, this.state.updatedAt);
  }

  async listOffers(): Promise<readonly MonetizationOffer[]> {
    return MONETIZATION_CATALOG;
  }

  async purchase(request: PurchaseRequest): Promise<PurchaseResult> {
    const prior = this.state.journal[request.idempotencyKey];
    if (prior) return prior as PurchaseResult;
    if (this.scenario === 'delayed_success')
      await new Promise((resolve) => setTimeout(resolve, 180));
    const offer = MONETIZATION_CATALOG.find((candidate) => candidate.id === request.offerId);
    let result: PurchaseResult;
    if (!offer) {
      result = {
        ok: false,
        code: 'unavailable',
        message: 'That QA offer is unavailable.',
        retryable: false,
        wallet: await this.getWallet(),
      };
    } else if (this.scenario === 'offline') {
      result = {
        ok: false,
        code: 'offline',
        message: 'The QA wallet could not be reached. Keep the free version or retry.',
        retryable: true,
        wallet: await this.getWallet(),
      };
    } else if (this.scenario === 'cancelled') {
      result = {
        ok: false,
        code: 'cancelled',
        message: 'Purchase cancelled. The free version is unchanged.',
        retryable: false,
        wallet: await this.getWallet(),
      };
    } else if (this.scenario === 'revoked') {
      result = {
        ok: false,
        code: 'revoked',
        message: 'This QA entitlement is no longer available.',
        retryable: false,
        wallet: await this.getWallet(),
      };
    } else if (
      this.scenario === 'insufficient_balance' ||
      this.state.balance < offer.priceCredits
    ) {
      result = {
        ok: false,
        code: 'insufficient_balance',
        message: `You need ${offer.priceCredits} Credits. The free version is still ready.`,
        retryable: false,
        wallet: await this.getWallet(),
      };
    } else {
      const nextBalance = this.state.balance - offer.priceCredits;
      const durableEntitlements: MonetizationOfferId[] =
        request.offerId === 'coaster_pass' ? ['coaster_pass'] : [];
      result = {
        ok: true,
        offerId: request.offerId,
        transactionId: `qa-purchase:${request.idempotencyKey}`,
        wallet: wallet(nextBalance),
        durableEntitlements,
      };
      this.state = {
        ...this.state,
        balance: nextBalance,
        passOwned: this.state.passOwned || request.offerId === 'coaster_pass',
        updatedAt: result.wallet.updatedAt,
      };
    }
    this.record(request.idempotencyKey, result);
    return this.scenario === 'duplicate_response' ? structuredClone(result) : result;
  }

  async showRewardedAd(request: RewardedAdRequest): Promise<RewardedAdResult> {
    const prior = this.state.journal[request.idempotencyKey];
    if (prior) return prior as RewardedAdResult;
    if (this.scenario === 'delayed_success')
      await new Promise((resolve) => setTimeout(resolve, 180));
    const offer = MONETIZATION_CATALOG.find((candidate) => candidate.id === request.offerId);
    let result: RewardedAdResult;
    if (!offer?.rewardedAdAvailable) {
      result = {
        ok: false,
        code: 'unavailable',
        message: 'A rewarded ad is not available for this treatment.',
        retryable: false,
      };
    } else if (this.scenario === 'offline') {
      result = {
        ok: false,
        code: 'offline',
        message: 'The rewarded ad is unavailable. Keep the free version or retry.',
        retryable: true,
      };
    } else if (this.scenario === 'cancelled') {
      result = {
        ok: false,
        code: 'cancelled',
        message: 'Ad closed before completion. Nothing was unlocked.',
        retryable: false,
      };
    } else {
      result = {
        ok: true,
        offerId: request.offerId,
        completionId: `qa-ad:${request.idempotencyKey}`,
      };
    }
    this.record(request.idempotencyKey, result);
    return this.scenario === 'duplicate_response' ? structuredClone(result) : result;
  }

  async restoreEntitlements(): Promise<RestoreResult> {
    return {
      ok: true,
      durableEntitlements: this.state.passOwned ? ['coaster_pass'] : [],
      message: this.state.passOwned ? 'Coaster Pass restored.' : 'No purchases to restore.',
    };
  }

  private load(): SandboxStoredState {
    try {
      const parsed = JSON.parse(
        this.storage?.getItem(this.storageKey) ?? 'null',
      ) as SandboxStoredState | null;
      if (
        parsed?.version === STORAGE_VERSION &&
        parsed.viewerId === this.options.viewerId &&
        Number.isFinite(parsed.balance) &&
        typeof parsed.passOwned === 'boolean'
      )
        return parsed;
    } catch {
      // Corrupt QA state is replaced, never trusted.
    }
    return {
      version: STORAGE_VERSION,
      viewerId: this.options.viewerId,
      balance: Math.max(0, Math.round(this.options.initialBalance ?? 500)),
      passOwned: false,
      journal: {},
      updatedAt: new Date().toISOString(),
    };
  }

  private record(key: string, result: PurchaseResult | RewardedAdResult): void {
    this.state = {
      ...this.state,
      journal: { ...this.state.journal, [key]: result },
      updatedAt: new Date().toISOString(),
    };
    this.storage?.setItem(this.storageKey, JSON.stringify(this.state));
  }
}

export function createUnavailableMonetizationAdapter(): MonetizationAdapter {
  return new UnavailableMonetizationAdapter();
}

export function createLocalSandboxMonetizationAdapter(
  options: LocalSandboxMonetizationAdapterOptions,
): LocalSandboxMonetizationAdapter {
  return new LocalSandboxMonetizationAdapter(options);
}
